import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { lookupsJson } from '@ourbranch/lookups';
import { useFormikContext } from 'formik';

import Field from 'core/components/form/form.v2';
import { calculateGrainBinHeight } from 'common/helpers/farm-helpers';
import { usesLengthWidth } from '@ourbranch/farm-utils';

// Form options constants
const bqOptions = lookupsJson.farm.buildingQuality;
const occupancyUseTypeByStructureCategory = lookupsJson.farm.occupancyUseTypeByStructureCategory;

/**
 * Form fields for Silos/Grain Bins (buildingType === 'SG').
 *
 * Key business rules (GitHub Issue: #29355):
 * - Grain Bin (GHB): Auto-calculate Height from Diameter + Bushel Capacity, disable Height
 * - Corn Crib (CC), Commodity Shed (GS), Grain/Feed Handling Center (GH):
 *   Show Length + Width instead of Diameter, auto-calculate SqFt = Length × Width
 * - All SG types: Quality defaults to Standard ('S') and is disabled
 * - Field order: Bushel Capacity comes before Height
 */
const SilosGrainBinsFields = ({
  fieldName,
  buildingType,
  initialBuilding,
  disableRiskForm,
  shouldEnableDiameterHeight,
  shouldEnableSqFt,
  toastRCEWarning,
  ctOptions,
  occupancy
}) => {
  const { setFieldValue, values } = useFormikContext();
  // Skip the occupancy-clearing effect on the initial mount to prevent wiping
  // existing saved values when the form loads for editing an existing risk.
  const isMountedRef = useRef(false);

  const isGrainBin = occupancy === 'GHB';
  const isLengthWidth = usesLengthWidth(occupancy);

  const buildingPath = `${fieldName}.building`;
  const diameter = values && getNestedValue(values, `${buildingPath}.diameter`);
  const bushelCapacity = values && getNestedValue(values, `${buildingPath}.bushelCapacity`);
  const length = values && getNestedValue(values, `${buildingPath}.length`);
  const width = values && getNestedValue(values, `${buildingPath}.width`);

  // Auto-calculate height for Grain Bin (GHB) when diameter or bushel capacity changes
  useEffect(() => {
    if (isGrainBin && diameter && bushelCapacity) {
      const calculatedHeight = calculateGrainBinHeight(diameter, bushelCapacity);
      setFieldValue(`${buildingPath}.height`, calculatedHeight);
    }
  }, [isGrainBin, diameter, bushelCapacity, buildingPath, setFieldValue]);

  // Auto-calculate sqFt for CC/GS/GH when length or width changes
  useEffect(() => {
    if (!isMountedRef.current) {
      return;
    }
    if (isLengthWidth && length && width) {
      setFieldValue(`${buildingPath}.sqFt`, length * width);
    } else if (!isLengthWidth) {
      // Clear the LW-computed sqFt when switching away from CC/GS/GH
      // so the non-LW occupancy starts with no stale value
      setFieldValue(`${buildingPath}.sqFt`, null);
    }
  }, [isLengthWidth, length, width, buildingPath, setFieldValue]);

  // Clear diameter when switching to a length/width occupancy type, and vice versa.
  // The isMountedRef guard skips the initial render so existing saved field values
  // are not wiped when the form first loads for an existing risk (mount-time data loss fix).
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (isLengthWidth) {
      setFieldValue(`${buildingPath}.diameter`, null);
    } else if (occupancy) {
      setFieldValue(`${buildingPath}.length`, null);
      setFieldValue(`${buildingPath}.width`, null);
    }
  }, [occupancy, isLengthWidth, buildingPath, setFieldValue]);

  return (
    <>
      <Field
        xs={6}
        type="select"
        id={`${buildingPath}.occupancy`}
        name={`${buildingPath}.occupancy`}
        mode="light"
        label="Occupancy or use type"
        options={occupancyUseTypeByStructureCategory[buildingType]}
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.occupancy) {
            toastRCEWarning();
          }
        }}
      />
      <Field
        xs={6}
        type="numeric"
        id={`${buildingPath}.yearBuilt`}
        name={`${buildingPath}.yearBuilt`}
        mode="light"
        label="Year built in"
        format={{
          allowNegative: false
        }}
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.yearBuilt) {
            toastRCEWarning();
          }
        }}
      />

      {/* Diameter shown for non-length/width occupancies (GHB, GHT, GHH, GL) */}
      {!isLengthWidth && (
        <Field
          xs={4}
          type="numeric"
          id={`${buildingPath}.diameter`}
          name={`${buildingPath}.diameter`}
          mode="light"
          label="Diameter (ft)"
          disabled={disableRiskForm || !shouldEnableDiameterHeight}
          onChange={(val) => {
            if (val !== initialBuilding?.diameter) {
              toastRCEWarning();
            }
          }}
        />
      )}

      {/* Length and Width shown for CC, GS, GH */}
      {isLengthWidth && (
        <>
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.length`}
            name={`${buildingPath}.length`}
            mode="light"
            label="Length (ft)"
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.length) {
                toastRCEWarning();
              }
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.width`}
            name={`${buildingPath}.width`}
            mode="light"
            label="Width (ft)"
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.width) {
                toastRCEWarning();
              }
            }}
          />
        </>
      )}

      {/* Bushel Capacity before Height (reordered per #29355) */}
      <Field
        xs={4}
        type="numeric"
        id={`${buildingPath}.bushelCapacity`}
        name={`${buildingPath}.bushelCapacity`}
        mode="light"
        label="Bushel capacity"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.bushelCapacity) {
            toastRCEWarning();
          }
        }}
      />

      <Field
        xs={4}
        type="numeric"
        id={`${buildingPath}.height`}
        name={`${buildingPath}.height`}
        mode="light"
        label="Height (ft)"
        disabled={disableRiskForm || isGrainBin}
        onChange={(val) => {
          if (val !== initialBuilding?.height) {
            toastRCEWarning();
          }
        }}
      />

      <Field
        xs={4}
        type="numeric"
        id={`${buildingPath}.sqFt`}
        name={`${buildingPath}.sqFt`}
        mode="light"
        label="Total sqft"
        disabled={disableRiskForm || !shouldEnableSqFt || isLengthWidth}
        onChange={(val) => {
          if (val !== initialBuilding?.sqFt) {
            toastRCEWarning();
          }
        }}
      />

      {/* Quality is always disabled and defaults to Standard for all SG types */}
      <Field
        xs={6}
        type="select"
        id={`${buildingPath}.buildingQuality`}
        name={`${buildingPath}.buildingQuality`}
        mode="light"
        label="Quality"
        options={bqOptions}
        disabled
      />

      <Field
        xs={6}
        type="select"
        id={`${buildingPath}.constructionType`}
        name={`${buildingPath}.constructionType`}
        mode="light"
        label="Construction type"
        options={ctOptions}
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.constructionType) {
            toastRCEWarning();
          }
        }}
      />
    </>
  );
};

/**
 * Safely get a nested value from an object using a dot-separated path.
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

SilosGrainBinsFields.propTypes = {
  fieldName: PropTypes.string.isRequired,
  buildingType: PropTypes.string.isRequired,
  initialBuilding: PropTypes.object.isRequired,
  disableRiskForm: PropTypes.bool.isRequired,
  shouldEnableDiameterHeight: PropTypes.bool.isRequired,
  shouldEnableSqFt: PropTypes.bool.isRequired,
  toastRCEWarning: PropTypes.func.isRequired,
  ctOptions: PropTypes.array.isRequired,
  occupancy: PropTypes.string
};

export default SilosGrainBinsFields;
